import type { RoutePathOption } from '@gpx-spa/geo-domain'
import { SHIPMENT_STOP_ROLE_VISUAL } from '@gpx-spa/constants'
import { METERS_PER_MILE } from '@gpx-spa/ui'

/** Stop geofence radius: **0.05 mi** min/step … **5 mi** max (~8 km). */
export const COMPOSER_STOP_RADIUS_MIN_M = 0.05 * METERS_PER_MILE
export const COMPOSER_STOP_RADIUS_MAX_M = 5 * METERS_PER_MILE
export const COMPOSER_STOP_RADIUS_STEP_M = 0.05 * METERS_PER_MILE
/** Initial radius before Recent / user input (prototype default). */
export const COMPOSER_STOP_RADIUS_DEFAULT_M = 0.2 * METERS_PER_MILE

/** Corridor buffer along route: **0.5 mi** … **50 mi**. */
export const COMPOSER_CORRIDOR_BUFFER_MIN_M = 0.5 * METERS_PER_MILE
export const COMPOSER_CORRIDOR_BUFFER_MAX_M = 50 * METERS_PER_MILE
export const COMPOSER_CORRIDOR_BUFFER_STEP_M = 0.05 * METERS_PER_MILE
export const COMPOSER_CORRIDOR_BUFFER_DEFAULT_M = METERS_PER_MILE

/** Default map center (Detroit) when fitBounds is not used. */
export const COMPOSER_MAP_DEFAULT_CENTER = { lat: 42.3314, lng: -83.0458 } as const

export const COMPOSER_DEBOUNCE = {
  corridorBufferMs: 280,
  geometryQualityMs: 220,
  /** Shipment create: debounce after stop order/coords change before auto route request. */
  autoRouteMs: 450,
  /** Shipment create wizard: debounce before re-applying stop-time autofill after route/path changes. */
  autofillTimesMs: 280,
  /** Safety cap when overview fitBounds cannot resolve bounds (missing layers). */
  mapOverviewFitMaxAttempts: 8,
} as const

/** Shared recent radii for all composer stop fields. */
export const SHIPMENT_COMPOSER_STOP_RADIUS_RECENT_KEY =
  'gpx-app/shipment-composer/stop-radius-recent'

/** Legacy key — read once when migrating browser localStorage prefs. */
export const SHIPMENT_COMPOSER_STOP_RADIUS_RECENT_KEY_LEGACY =
  'gpx-app/shipment-create/stop-radius-recent'

/** Recent corridor buffer widths (metres), separate from stop radii. */
export const SHIPMENT_COMPOSER_CORRIDOR_BUFFER_RECENT_KEY =
  'gpx-app/shipment-composer/corridor-buffer-recent'

export const SHIPMENT_COMPOSER_CORRIDOR_BUFFER_RECENT_KEY_LEGACY =
  'gpx-app/shipment-create/corridor-buffer-recent'

/** Migrate a localStorage key from legacy shipment-create namespace to composer. */
export function migrateFormLocalStorageKey(
  nextKey: string,
  legacyKey: string,
): void {
  if (typeof localStorage === 'undefined') return
  if (localStorage.getItem(nextKey) != null) return
  const legacy = localStorage.getItem(legacyKey)
  if (legacy == null) return
  localStorage.setItem(nextKey, legacy)
  localStorage.removeItem(legacyKey)
}

export const PATH_ALTERNATIVE_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#ca8a04',
  '#9333ea',
  '#0891b2',
] as const

export const COMPOSER_LAYER_IDS = {
  stopStart: 'composer-stop-start',
  stopEnd: 'composer-stop-end',
  stopWaypoint: (index: number) => `composer-stop-waypoint-${index}`,
  /** Numbered circles (1…n) aligned with stop cards — see {@link SHIPMENT_STOP_ROLE_VISUAL}. */
  stopNumberPins: 'composer-stop-number-pins',
  devicePositions: 'composer-device-positions',
  buffer: (pathId: string) => `composer-buffer-${encodeURIComponent(pathId)}`,
  path: (pathId: string) => `composer-path-${encodeURIComponent(pathId)}`,
} as const

/** Leaflet GeoJSON feature styling for composer map overlays (aligned with {@link SHIPMENT_STOP_ROLE_VISUAL}). */
export const COMPOSER_MAP_STYLES = {
  stopStart: SHIPMENT_STOP_ROLE_VISUAL.origin.map,
  stopEnd: SHIPMENT_STOP_ROLE_VISUAL.destination.map,
  stopWaypoint: SHIPMENT_STOP_ROLE_VISUAL.waypoint.map,
  buffer: (color: string) => ({
    fill: color,
    fillOpacity: 0.12,
    stroke: color,
    strokeOpacity: 0.35,
    strokeWidth: 1,
  }),
  pathLine: (color: string, options?: { lineDashArray?: number[] }) => ({
    lineColor: color,
    lineWidth: 5,
    lineOpacity: 0.92,
    ...(options?.lineDashArray?.length ? { lineDashArray: options.lineDashArray } : {}),
  }),
} as const

/** Solid “main” path vs dashed alternatives (see {@link alternativePathDashPattern}). */
export function isFormPrimaryPath(path: RoutePathOption, index: number): boolean {
  if (path.meta?.provider === 'learned') return false
  if (path.id != null && path.id !== '') {
    return path.id === 'primary'
  }
  return index === 0
}

/**
 * Alternatives only — canonical dash patterns (multiples of `lineWidth`).
 * Curated for overlap: short-to-medium dashes and gaps ≥ dash so underlays show in the “holes”.
 * Avoid long-dash/short-gap ratios (they read almost solid when stacked).
 */
const ALTERNATIVE_DASH_PATTERNS: number[][] = [
  [2, 3],
  [2, 4],
  [3, 4],
  [2, 2],
  [3, 5],
  [1, 2],
  [2, 3, 2, 4],
  [3, 3, 2, 3],
  [2, 5],
  [3, 6],
]

/** Stable pick by path id — same id always maps to the same pattern (reproducible, not noisy per frame). */
export function alternativePathDashPattern(pathId: string): number[] {
  let h = 0
  for (let i = 0; i < pathId.length; i++) {
    h = (Math.imul(31, h) + pathId.charCodeAt(i)) | 0
  }
  const u = h >>> 0
  return ALTERNATIVE_DASH_PATTERNS[u % ALTERNATIVE_DASH_PATTERNS.length]
}
